import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Compass } from "lucide-react";
import { BRAND } from "../lib/brand";
import { setBetaCode } from "../lib/beta";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Public-facing "we're in private beta" placeholder shown in place of the
 * real Login / Signup pages while the gate is active. Includes a small
 * "Have a beta code?" form so testers who arrive without the magic link can
 * still type the code in.
 */
export default function ComingSoon({ pageTitle = "Coming soon" }) {
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setSubmitting(true);
        try {
            const { data } = await api.get("/beta/check", { params: { code: code.trim() } });
            if (data?.valid) {
                setBetaCode(code.trim());
                toast.success("Welcome, beta tester");
                window.location.reload();
            } else {
                toast.error("That code isn't valid.");
            }
        } catch {
            toast.error("Could not verify code. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-midnight-pine text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{
                backgroundImage:
                    "radial-gradient(circle at 30% 20%, rgba(242,106,46,0.4), transparent 50%), radial-gradient(circle at 70% 80%, rgba(31,86,75,0.5), transparent 60%)",
            }} />
            <div className="relative z-10 min-h-screen flex flex-col">
                <header className="px-4 sm:px-8 py-6 flex items-center justify-between">
                    <Link to="/" data-testid="coming-soon-brand" className="flex items-center gap-3">
                        <img src={BRAND.logoUrl} alt={BRAND.name} className="w-10 h-10 object-contain" />
                        <div>
                            <div className="font-display font-black text-xl text-white leading-none tracking-tight">
                                {BRAND.nameWordOne}
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-trail-tan">
                                {BRAND.nameWordTwo}
                            </div>
                        </div>
                    </Link>
                    <Link
                        to="/"
                        data-testid="coming-soon-back"
                        className="inline-flex items-center gap-2 text-sm font-bold text-white/80 hover:text-ember-orange transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to home
                    </Link>
                </header>

                <main className="flex-1 flex items-center px-4 sm:px-8" data-testid="coming-soon-page">
                    <div className="max-w-2xl w-full mx-auto">
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-ember-orange mb-4">
                            Private beta · {pageTitle}
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
                            We&rsquo;re lacing up.<br />
                            <span className="text-ember-orange">Public launch coming soon.</span>
                        </h1>
                        <p className="text-base sm:text-lg text-white/75 leading-relaxed mb-10 max-w-xl">
                            {BRAND.name} is in private beta while we polish trail reports,
                            community tools, and safety features with a small group of testers.
                            If you&rsquo;d like an invite, leave us a note and we&rsquo;ll be in touch.
                        </p>

                        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-lg p-6 max-w-md">
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-trail-tan mb-3">
                                Beta tester?
                            </div>
                            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2">
                                <input
                                    data-testid="coming-soon-code-input"
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="Paste your invite code"
                                    autoComplete="off"
                                    className="flex-1 px-4 py-2.5 rounded-md bg-midnight-pine/70 border border-white/20 text-white placeholder:text-white/40 focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/30"
                                />
                                <button
                                    type="submit"
                                    data-testid="coming-soon-code-submit"
                                    disabled={submitting || !code.trim()}
                                    className="px-5 py-2.5 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 transition-all"
                                >
                                    {submitting ? "Checking…" : "Unlock"}
                                </button>
                            </form>
                            <p className="text-xs text-white/50 mt-3">
                                Got a magic link from us? Just open it &mdash; no copy/paste needed.
                            </p>
                        </div>

                        <div className="mt-10 text-sm text-white/60">
                            Questions? <a href={`mailto:${BRAND.legal.contactEmail}`} className="text-ember-orange font-semibold hover:underline">{BRAND.legal.contactEmail}</a>
                        </div>
                    </div>
                </main>

                <footer className="px-4 sm:px-8 py-6 text-xs text-white/40 flex items-center gap-2">
                    <Compass className="w-3.5 h-3.5" />
                    © {new Date().getFullYear()} {BRAND.legal.entity}
                </footer>
            </div>
        </div>
    );
}
